import { ref } from 'vue';

/**
 * The columns a person last exported from a list page, one choice per record
 * type. The list page's Export opens a column list. That list starts from this
 * choice, and pressing its Export button stores the new one here.
 *
 * Kept apart from the list's Column settings. Column settings decide what the
 * table shows. This decides what the spreadsheet holds. A record type with no
 * stored choice starts from the columns on screen.
 *
 * The track lists keep their own choice in `trackListColumns.ts`, because their
 * columns are a fixed set and are shared by every track list.
 */

const STORAGE_KEY = 'search-export-columns:v1';

function readStoredChoices(): Record<string, string[]> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return {};
    }
    const parsed: unknown = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const choices: Record<string, string[]> = {};
    for (const [entitySlug, columnIds] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(columnIds)) {
        choices[entitySlug] = columnIds.filter((columnId): columnId is string => typeof columnId === 'string');
      }
    }
    return choices;
  } catch {
    return {};
  }
}

const storedChoices = ref<Record<string, string[]>>(readStoredChoices());

/** The stored export columns for one record type, or null when there are none. */
export function storedSearchExportColumns(entitySlug: string): string[] | null {
  const columnIds = storedChoices.value[entitySlug];
  return columnIds && columnIds.length > 0 ? [...columnIds] : null;
}

/** Store the export columns for one record type. */
export function storeSearchExportColumns(entitySlug: string, columnIds: string[]): void {
  storedChoices.value = { ...storedChoices.value, [entitySlug]: [...columnIds] };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storedChoices.value));
  } catch {
    // Private mode or no storage. The choice still lasts until the page reloads.
  }
}

/** Test hook: forget every stored choice, in memory and in storage. */
export function resetSearchExportColumnsForTesting(): void {
  storedChoices.value = {};
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to remove.
  }
}
